/**
 * PfandModal – BottomSheet für den Flaschensammler-Pfand-Loop (NT-PFAND-SAMMLER-01, Schritt 6).
 * Eigenständige, body-attachte Komponente (Muster wie PoliceEventModal.ts) — nutzt die
 * gemeinsame `.modal-backdrop`/`.modal`-Hülle (dashboard.css) und das `markt-qty-*`-Mengenwähler-
 * Muster (markt.ts). Schnittstelle: `openPfandModal(onCargoChanged)` aus dashboard.ts aufrufen.
 */

import { api, ApiError } from '../services/api.js';
import { showToast } from '../utils/toast.js';
import { refreshBalance } from '../utils/refreshBalance.js';

const MODAL_ID = 'pfand-modal';

// Reine Anzeige-Konstante — Backend: pfand.constants.ts::PFAND_GESCHAEFT_SPAETI_KEY (kein Shared-Import).
const SPAETI_KEY = 'spaeti';
// Backend: pfand.constants.ts::PFAND_SPAETI_DEFEKT_MESSAGE (kein Shared-Import, reiner Anzeigetext).
const SPAETI_DEFEKT_MESSAGE = 'AUTOMAT DEFEKT — SEGMENTATION FAULT (KASTEN DUMPED)';

interface PfandStationView {
  key: string;
  label: string;
  status: 'FREI' | 'WARTUNG' | 'DEFEKT';
  maintenanceUntil: string | null;
  unitPriceCents: string | null;
  advertisedPriceCents: string | null;
  capacityUsed: number | null;
  capacityMax: number | null;
  /** Nur beim Späti gesetzt — Zeitpunkt des nächsten Betriebszustand-Wurfs (Countdown bei DEFEKT). */
  spaetiNextRollAt: string | null;
}

interface PfandStatusResponse {
  stations: PfandStationView[];
  boerse: { priceCents: string; trend: '▲' | '▼' | '►' };
  cargo: { total: number; totalCapacity: number };
}

interface PfandAbgebenResult {
  stationKey: string;
  accepted: number;
  remainingInCargo: number;
  unitPriceCents: string;
  totalCents: string;
  outcome?: 'DEFEKT' | 'PREMIUM_SUCCESS';
}

let status: PfandStatusResponse | null = null;
let selectedStationKey: string | null = null;
let quantity = 0;
let submitting = false;
let countdownTimer: ReturnType<typeof setInterval> | null = null;
let cargoChangedCb: (() => void) | null = null;

function esc(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

function fmtCents(cents: string | number): string {
  return (Number(cents) / 100).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function fmtCountdown(isoUntil: string): string {
  const ms = new Date(isoUntil).getTime() - Date.now();
  if (ms <= 0) return '00:00';
  const totalSeconds = Math.ceil(ms / 1000);
  const m = Math.floor(totalSeconds / 60);
  const s = totalSeconds % 60;
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

export function removePfandModal(): void {
  if (countdownTimer !== null) { clearInterval(countdownTimer); countdownTimer = null; }
  document.getElementById(MODAL_ID)?.remove();
  status = null;
  cargoChangedCb = null;
}

/** Öffnet das Modal und lädt Stations-/Börsen-/Cargo-Status. onCargoChanged wird nach jeder Abgabe aufgerufen (Job-Card-Zähler refreshen). */
export async function openPfandModal(onCargoChanged?: () => void): Promise<void> {
  cargoChangedCb = onCargoChanged ?? null;
  removePfandModal();
  quantity = 0;
  selectedStationKey = null;

  const overlay = document.createElement('div');
  overlay.id = MODAL_ID;
  overlay.className = 'modal-backdrop';
  document.body.appendChild(overlay);

  await reload();
  if (status) {
    quantity = status.cargo.total;
    selectedStationKey = status.stations.find((s) => s.status === 'FREI')?.key
      ?? status.stations[0]?.key
      ?? null;
  }
  render();
  attachListeners(overlay);
  countdownTimer = setInterval(updateCountdowns, 1_000);
}

async function reload(): Promise<void> {
  try {
    status = await api.get<PfandStatusResponse>('/pfand/stations');
  } catch {
    showToast('Pfand-Stationen konnten nicht geladen werden.', 'error');
  }
}

function updateCountdowns(): void {
  if (!status) return;
  for (const st of status.stations) {
    // Nur WARTUNG zeigt noch einen Countdown — DEFEKT (Späti) zeigt bewusst nur den reinen
    // Status ohne Timer (Chris-Feedback 2026-08-29).
    if (st.status !== 'WARTUNG' || !st.maintenanceUntil) continue;
    const el = document.querySelector<HTMLElement>(`[data-pfand-countdown="${st.key}"]`);
    if (el) el.textContent = fmtCountdown(st.maintenanceUntil);
  }
}

function render(): void {
  const overlay = document.getElementById(MODAL_ID);
  if (!overlay || !status) return;

  const cargoTotal = status.cargo.total;
  const cargoCapacity = status.cargo.totalCapacity;
  const empty = cargoTotal <= 0;

  const stationsHtml = status.stations.map((st) => {
    const isSelected = st.key === selectedStationKey;
    const isDisabled = st.status === 'WARTUNG';
    // DEFEKT (nur Späti, DEF-B): der Betriebszustand ist bekannt (nächster Wurf in
    // spaetiNextRollAt), wird aber bewusst OHNE Countdown angezeigt (Chris-Feedback
    // 2026-08-29 — der 20-Minuten-Timer nervte, reiner FREI/DEFEKT-Status reicht). Bleibt
    // trotzdem klickbar, ein Versuch liefert konsistent die "AUTOMAT DEFEKT"-Meldung ohne
    // jede Konsequenz (kein Bestandsverlust).
    const statusHtml = st.status === 'FREI'
      ? '<span class="pfand-station-status pfand-station-status--frei">FREI</span>'
      : st.status === 'DEFEKT'
        ? '<span class="pfand-station-status pfand-station-status--defekt">DEFEKT</span>'
        : `<span class="pfand-station-status pfand-station-status--wartung">WARTUNG&nbsp;<span data-pfand-countdown="${esc(st.key)}">${st.maintenanceUntil ? fmtCountdown(st.maintenanceUntil) : '--:--'}</span></span>`;
    // Späti (DEF-B) bewirbt sichtbar seinen Bestpreis — die ganze Pointe des Easter Eggs.
    const advertisedHtml = st.key === SPAETI_KEY && st.advertisedPriceCents
      ? `<span class="pfand-station-advertised">${fmtCents(st.advertisedPriceCents)}&nbsp;₦/STK*</span>`
      : '';
    // Realer Stückpreis (Chris-Feedback 2026-07-30) — für jede Station außer Späti.
    const priceHtml = st.unitPriceCents !== null
      ? `<span class="pfand-station-price">${fmtCents(st.unitPriceCents)}&nbsp;₦/STK</span>`
      : '';
    // Restkapazität (Chris-Feedback 2026-08-05) — erzeugt sichtbaren Druck vor WARTUNG.
    // capacityMax=null (Bahnhof, Späti) → kein Limit, keine Anzeige. Während WARTUNG selbst
    // steht capacityUsed serverseitig bereits wieder auf 0 (Reset beim Auslösen) — die Zahl
    // wäre dort irreführend ("voll frei"), der WARTUNG-Countdown sagt bereits alles Nötige.
    let capacityHtml = '';
    if (st.status === 'FREI' && st.capacityMax !== null && st.capacityUsed !== null) {
      const remaining = Math.max(0, st.capacityMax - st.capacityUsed);
      const ratio = st.capacityUsed / st.capacityMax;
      const capClass = ratio >= 0.9 ? ' pfand-station-capacity--critical' : ratio >= 0.7 ? ' pfand-station-capacity--warn' : '';
      capacityHtml = `<span class="pfand-station-capacity${capClass}">NOCH ${String(remaining)} VON ${String(st.capacityMax)} FREI</span>`;
    }
    return `
      <button type="button" class="pfand-station-row${isSelected ? ' pfand-station-row--selected' : ''}" data-pfand-station="${esc(st.key)}" ${isDisabled ? 'disabled' : ''}>
        <span class="pfand-station-name">${esc(st.label)}${priceHtml}${advertisedHtml}</span>
        ${statusHtml}
        ${capacityHtml}
      </button>
    `;
  }).join('');

  const canSubmit = !empty && !submitting && selectedStationKey !== null && quantity > 0;

  overlay.innerHTML = `
    <div class="modal modal--narrow" role="dialog" aria-modal="true" aria-label="Pfand abgeben">
      <div class="modal-header">
        <h2>FLASCHEN ABGEBEN</h2>
        <p class="modal-subtitle">BÖRSE HEUTE: ${fmtCents(status.boerse.priceCents)}&nbsp;₦&nbsp;${status.boerse.trend} &middot; BESTAND: ${String(cargoTotal)}/${String(cargoCapacity)}</p>
      </div>
      <div class="modal-body">
        <div class="pfand-station-list">${stationsHtml}</div>
        ${status.stations.some((s) => s.key === SPAETI_KEY)
          ? '<p class="pfand-spaeti-hint">* Beworbener Bestpreis — Automat ist fast immer defekt.</p>'
          : ''}

        ${empty
          ? '<p class="markt-modal-warn">KEINE FLASCHEN — ERST SAMMELN</p>'
          : `
          <div class="markt-modal-row">
            <label class="markt-modal-label">MENGE</label>
            <div class="markt-qty-row">
              <button class="markt-qty-btn" data-pfand-qty="-1">-</button>
              <input type="number" class="markt-qty-input" id="pfand-qty-input" min="1" max="${String(cargoTotal)}" value="${String(quantity)}" />
              <button class="markt-qty-btn" data-pfand-qty="+1">+</button>
              <button class="markt-qty-btn markt-qty-btn--max" data-pfand-qty-max="${String(cargoTotal)}">MAX</button>
            </div>
          </div>
        `}
      </div>
      <div class="modal-actions">
        <button class="btn btn--primary" id="pfand-confirm-abgeben" ${canSubmit ? '' : 'disabled'}>${submitting ? '...' : 'ABGEBEN'}</button>
        <button class="btn" data-close-modal>SCHLIESSEN</button>
      </div>
    </div>
  `;
}

function attachListeners(overlay: HTMLElement): void {
  overlay.addEventListener('click', (e) => {
    const target = e.target as Element;

    if (target === overlay || target.closest('[data-close-modal]')) {
      removePfandModal();
      return;
    }

    const stationBtn = target.closest<HTMLButtonElement>('[data-pfand-station]');
    if (stationBtn !== null && !stationBtn.disabled) {
      selectedStationKey = stationBtn.dataset.pfandStation ?? null;
      render();
      return;
    }

    const qtyBtn = target.closest<HTMLButtonElement>('[data-pfand-qty]');
    if (qtyBtn !== null && status) {
      const delta = parseInt(qtyBtn.dataset.pfandQty ?? '0', 10);
      quantity = Math.max(1, Math.min(status.cargo.total, quantity + delta));
      render();
      return;
    }

    const maxBtn = target.closest<HTMLButtonElement>('[data-pfand-qty-max]');
    if (maxBtn !== null) {
      quantity = parseInt(maxBtn.dataset.pfandQtyMax ?? '0', 10);
      render();
      return;
    }

    if (target.closest('#pfand-confirm-abgeben') !== null) {
      void handleAbgeben();
      return;
    }
  });

  overlay.addEventListener('input', (e) => {
    const target = e.target as HTMLElement;
    if (target.id === 'pfand-qty-input' && status) {
      const raw = parseInt((target as HTMLInputElement).value, 10);
      quantity = Number.isFinite(raw) ? Math.max(1, Math.min(status.cargo.total, raw)) : 1;
    }
  });
}

async function handleAbgeben(): Promise<void> {
  if (!status || !selectedStationKey || quantity <= 0 || submitting) return;
  const stationKey = selectedStationKey;
  const requestedQty = quantity;
  const station = status.stations.find((s) => s.key === stationKey);

  submitting = true;
  // Kein voller render() hier (Chris-Feedback 2026-07-30: sichtbares "Zucken" bei jedem Klick,
  // Ursache war ein voller innerHTML-Replace sofort beim Klick UND ein zweiter nach Abschluss —
  // Muster wie der bereits behobene Bug in NT-UI-LAYOUT-RERENDER-FIX-01). Nur der Button wird
  // direkt gesperrt, der abschließende render() unten (nach reload()) bleibt der einzige Replace.
  const confirmBtn = document.querySelector<HTMLButtonElement>('#pfand-confirm-abgeben');
  if (confirmBtn) {
    confirmBtn.disabled = true;
    confirmBtn.textContent = '...';
  }

  try {
    const result = await api.post<PfandAbgebenResult>('/pfand/abgeben', { stationKey, quantity: requestedQty });

    if (result.outcome === 'DEFEKT') {
      showToast(SPAETI_DEFEKT_MESSAGE, 'error');
    } else if (result.outcome === 'PREMIUM_SUCCESS') {
      showToast(
        `TREFFER! ${String(result.accepted)} FLASCHEN → ${fmtCents(result.totalCents)} N (${fmtCents(result.unitPriceCents)} N/STÜCK PREMIUM)`,
        'info',
      );
    } else if (result.accepted <= 0) {
      showToast(`${station?.label ?? 'Station'} kann gerade nichts annehmen.`, 'error');
    } else if (result.accepted < requestedQty) {
      showToast(
        `${station?.label ?? 'STATION'} VOLL — ${String(result.accepted)} ANGENOMMEN, ${String(requestedQty - result.accepted)} ZURÜCK INS CARGO.`,
        'info',
      );
    } else {
      showToast(
        `${String(result.accepted)} FLASCHEN → ${fmtCents(result.totalCents)} N (${fmtCents(result.unitPriceCents)} N/STÜCK)`,
        'info',
      );
    }

    if (Number(result.totalCents) > 0) void refreshBalance();
    await reload();
    quantity = status.cargo.total;
    selectedStationKey = status.stations.find((s) => s.status === 'FREI')?.key ?? status.stations[0]?.key ?? null;
    cargoChangedCb?.();
  } catch (err) {
    showToast(err instanceof ApiError ? err.message : 'Abgabe fehlgeschlagen.', 'error');
  } finally {
    submitting = false;
    render();
  }
}
